'use client';

import { useState, type ReactNode } from 'react';
import { Reorder, useDragControls, motion, AnimatePresence } from 'framer-motion';
import { GripVertical, Trash2, Copy, Plus } from 'lucide-react';

import { usePageBuilderContext } from '../page-builder-context';
import type { ContentBlock } from '@/lib/types/page-builder';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface BlockWrapperProps {
  block: ContentBlock;
  children: ReactNode;
  className?: string;
  isFirst?: boolean;
  isLast?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function BlockWrapper({ block, children, className, isFirst, isLast }: BlockWrapperProps) {
  const {
    deleteBlock,
    duplicateBlock,
    openInsertMenu,
    focusedBlockId,
    setFocusedBlockId,
    errors,
  } = usePageBuilderContext();

  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isFocused = focusedBlockId === block.id;
  const hasError = errors.has(block.id);

  const dragControls = useDragControls();

  const showControls = (isHovered || isFocused) && !isDragging;

  return (
    <Reorder.Item
      value={block}
      id={block.id}
      dragListener={false}
      dragControls={dragControls}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        boxShadow: isDragging
          ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          : '0 0 0 0 rgba(0, 0, 0, 0)',
      }}
      exit={{ opacity: 0, height: 0 }}
      transition={{
        duration: 0.15,
        ease: 'easeInOut',
      }}
      whileDrag={{
        zIndex: 50,
        cursor: 'grabbing',
      }}
      className={cn(
        'relative group',
        isDragging && 'z-50',
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => !isDragging && setFocusedBlockId(block.id)}
    >
      {/* Drag handle + Controls (left side) */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="absolute left-0 top-0 -translate-x-full pr-2 flex items-start gap-1 pt-2"
          >
            {/* Drag handle */}
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                dragControls.start(e);
              }}
              className={cn(
                'w-6 h-8 flex items-center justify-center rounded text-foreground-tertiary',
                'hover:bg-muted hover:text-foreground cursor-grab active:cursor-grabbing',
                'transition-colors duration-150 touch-none'
              )}
              aria-label="Перетащить блок"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right side controls */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="absolute right-0 top-0 translate-x-full pl-2 flex flex-col gap-1 pt-2"
          >
            {/* Add block after */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openInsertMenu(block.id);
              }}
              className={cn(
                'w-7 h-7 flex items-center justify-center rounded-md',
                'text-foreground-tertiary hover:text-foreground hover:bg-muted',
                'transition-colors duration-150'
              )}
              aria-label="Добавить блок"
            >
              <Plus className="h-4 w-4" />
            </button>

            {/* Duplicate */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                duplicateBlock(block.id);
              }}
              className={cn(
                'w-7 h-7 flex items-center justify-center rounded-md',
                'text-foreground-tertiary hover:text-foreground hover:bg-muted',
                'transition-colors duration-150'
              )}
              aria-label="Дублировать блок"
            >
              <Copy className="h-4 w-4" />
            </button>

            {/* Delete */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                deleteBlock(block.id);
              }}
              className={cn(
                'w-7 h-7 flex items-center justify-center rounded-md',
                'text-foreground-tertiary hover:text-destructive hover:bg-destructive/10',
                'transition-colors duration-150'
              )}
              aria-label="Удалить блок"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div
        className={cn(
          'transition-all duration-200 border-2 border-dotted border-x-border overflow-hidden',
          isFirst && 'border-t-border rounded-t-xl',
          !isFirst && 'border-t-transparent',
          isLast && 'border-b-border rounded-b-xl',
          !isLast && 'border-b-transparent',
          hasError && 'border-destructive/50'
        )}
      >
        {children}
      </div>

      {/* Error message */}
      <AnimatePresence>
        {hasError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <p className="text-xs text-destructive mt-2 px-1">
              {errors.get(block.id)?.[0]}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </Reorder.Item>
  );
}
